import React, { useState } from "react";
import { ChevronRight, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Modal } from "@multiagentbook/ui";
import type { BookTOC } from "./toc-types";

interface BookCoverProps {
  size?: "mobile" | "desktop";
  showTOCButton?: boolean;
  width?: string; // Custom width class like "w-80", "w-64", etc.
}

const BookCover: React.FC<BookCoverProps> = ({
  size = "mobile",
  showTOCButton = true,
  width,
}) => {
  const { t } = useTranslation("home");
  const { i18n } = useTranslation("toc");
  const [showTOCModal, setShowTOCModal] = useState(false);

  // Get TOC data from translations - cast to BookTOC type for type safety
  const tocData = i18n.getResourceBundle(i18n.language, "toc") as
    | BookTOC
    | undefined;
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set()
  );
  const [expandedChapters, setExpandedChapters] = useState<Set<string>>(
    new Set()
  );

  // Use custom width if provided, otherwise fall back to size-based classes
  const sizeClasses = width
    ? `${width} h-auto`
    : size === "mobile"
    ? "w-48 h-auto"
    : "w-64 h-auto";
  const dimensions =
    size === "mobile"
      ? { width: 96, height: 120 }
      : { width: 160, height: 200 };

  const handleTOCClick = () => {
    setShowTOCModal(true);
  };

  const handleTOCClose = () => {
    setShowTOCModal(false);
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const toggleChapter = (chapterId: string) => {
    setExpandedChapters((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(chapterId)) {
        newSet.delete(chapterId);
      } else {
        newSet.add(chapterId);
      }
      return newSet;
    });
  };

  return (
    <>
      <div className="flex flex-col items-center">
        <div
          className="relative group cursor-pointer"
          onClick={handleTOCClick}
        >
          <img
            src="/images/bookcover.webp"
            alt="Designing Multi-Agent Systems Book Cover"
            className={`${sizeClasses} rounded-lg shadow-lg border border-tertiary
                       transition-all duration-300 ease-in-out
                       group-hover:scale-105 group-hover:shadow-xl group-hover:border-blue-300`}
            width={dimensions.width}
            height={dimensions.height}
            loading="eager"
          />

          {/* TOC Button - positioned on top right */}
          {showTOCButton && (
            <button
              className="absolute -top-2 -right-2 bg-blue-600 hover:bg-blue-700 text-white
                     px-2 py-1 rounded-full shadow-lg flex items-center justify-center gap-1
                     transition-all duration-200 transform hover:scale-105
                     border-2 border-white text-xs font-medium"
              onClick={handleTOCClick}
              title={t("bookCover.tableOfContents")}
            >
              <svg
                className="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 10h16M4 14h16M4 18h16"
                />
              </svg>
              <span>{t("bookCover.lookInside")}</span>
            </button>
          )}

          {/* Hover overlay with text */}
          <div
            className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100
                         transition-opacity duration-300 rounded-lg flex items-center justify-center"
          >
            <div className="text-white text-center">
              <div className="text-sm font-medium mb-1">
                {t("bookCover.viewContents")}
              </div>
              <div className="text-xs opacity-80">
                {t("bookCover.clickToSee")}
              </div>
            </div>
          </div>
        </div>

        {/* #1 New Release Badge */}
        <a
          href="https://www.amazon.com/dp/B0G2BCQQJY"
          target="_blank"
          rel="noreferrer"
          className={`mt-2 block text-center text-xs px-2 py-1.5 bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 rounded-md border border-indigo-200 font-medium hover:from-indigo-100 hover:to-purple-100 hover:border-indigo-300 transition-all duration-200 ${width ? width : size === "mobile" ? "w-48" : "w-64"}`}
        >
          {t("bookCover.newRelease")}
        </a>
      </div>

      {/* TOC Modal */}
      {showTOCModal && (
        <Modal
          isOpen={showTOCModal}
          onClose={handleTOCClose}
          title={t("bookCover.tableOfContents")}
          size="xl"
        >
          <div className="max-h-[80vh] overflow-y-auto p-6">
            <div className="space-y-8">
              {tocData?.sections.map((section) => (
                <div key={section.id}>
                  {/* Section Header */}
                  <div
                    className="flex items-start gap-2 cursor-pointer group mb-4"
                    onClick={() =>
                      section.description && toggleSection(section.id)
                    }
                  >
                    {section.description && (
                      <div className="mt-1 text-gray-400 group-hover:text-primary transition-colors">
                        {expandedSections.has(section.id) ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </div>
                    )}
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-primary border-b border-secondary pb-2">
                        {section.title}
                      </h4>
                    </div>
                  </div>

                  {/* Section Description */}
                  {section.description && expandedSections.has(section.id) && (
                    <div className="ml-6 mb-4 text-sm text-gray-600 dark:text-gray-400 italic">
                      {section.description}
                    </div>
                  )}

                  {/* Chapters */}
                  <ul className="space-y-3">
                    {section.chapters.map((chapter) => (
                      <li key={chapter.id} className="ml-2">
                        <div
                          className="flex items-start gap-3 cursor-pointer group"
                          onClick={() =>
                            chapter.description && toggleChapter(chapter.id)
                          }
                        >
                          {chapter.description && (
                            <div className="mt-0.5 text-gray-400 group-hover:text-primary transition-colors">
                              {expandedChapters.has(chapter.id) ? (
                                <ChevronDown className="w-3.5 h-3.5" />
                              ) : (
                                <ChevronRight className="w-3.5 h-3.5" />
                              )}
                            </div>
                          )}
                          <span className="text-gray-400 font-mono text-sm mt-0.5 min-w-[2rem]">
                            {chapter.order.toString().padStart(2, "0")}.
                          </span>
                          <span className="text-primary group-hover:text-accent transition-colors flex-1">
                            {chapter.title}
                          </span>
                        </div>

                        {/* Chapter Description */}
                        {chapter.description &&
                          expandedChapters.has(chapter.id) && (
                            <div className="ml-12 mt-2 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                              {chapter.description}
                            </div>
                          )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default BookCover;
